import { Editor } from '@tinymce/tinymce-react'
import clsx from 'clsx'
import './RichTextEditor.css'

export type EditorProps = {
  id?: string
  name?: string
  initialValue?: string
  value?: string
  placeholder?: string
  onChange?: (content: string) => void
  onBlur?: () => void
  setIsLoading?: (isLoading: boolean) => void
  isLoading?: boolean
  height?: number
  minHeight?: number
  maxHeight?: number
  disabled?: boolean
  plugins?: string[]
  toolbar?: string
  menubar?: boolean
  resize?: boolean
  branding?: boolean
  className?: string
  dataCompId?: string
  hasError?: boolean
}

export const RichTextEditor = (props: EditorProps) => {
  const {
    id,
    name,
    initialValue = '',
    placeholder = '',
    onChange,
    onBlur,
    setIsLoading,
    isLoading,
    height = 250,
    minHeight = 250,
    maxHeight = 800,
    disabled = false,
    plugins = [
      'lists advlist code link table textcolor autoresize image'
    ],
    toolbar = 'undo redo | styleselect | alignleft aligncenter alignright alignjustify | bold italic underline | fontselect fontsizeselect | forecolor | bullist numlist outdent indent | link unlink | code | table | image | removeformat | more',
    menubar = false,
    resize = true,
    branding = false,
    className,
    dataCompId,
    hasError = false,
    ...otherProps
  } = props

  return (
    <div 
      className={clsx(
        className, 
        hasError && 'rich-text-editor-error',
        isLoading && 'tw:opacity-50'
      )}
      {...(dataCompId && { 'data-compid': `${dataCompId}-editor` })}
    >
      <Editor
        id={id}
        initialValue={initialValue}
        onEditorChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        init={{
          images_upload_url: '/uploads',
          automatic_uploads: true,
          height,
          min_height: minHeight,
          max_height: maxHeight,
          menubar,
          plugins,
          toolbar,
          toolbar_mode: 'sliding',
          referrer_policy: 'no-referrer',
          branding,
          resize,
          placeholder,
          block_formats: 'Paragraph=p; Header 1=h1; Header 2=h2; Header 3=h3; Header 4=h4; Header 5=h5; Header 6=h6; Preformatted=pre',
          content_css: 'default',
          content_style: `
            body {
              font-size: 1rem;
              line-height: 1.5;
              color: #374151; /* tw:text-gray-700 */
              background-color: white;
              margin: 0 1rem;
              padding: 0.75rem;
            }

            p {
              margin: 0 0 1em;
            }

            /* Placeholder styling for empty content */
            body[data-mce-placeholder]:not([data-mce-placeholder=""]):before {
              content: attr(data-mce-placeholder);
              color: #6b7280; /* tw:placeholder:text-gray-500 */
              position: absolute;
              top: 0.75rem;
              left: 0.75rem;
              pointer-events: none;
              opacity: 1; /* Remove opacity to match Tailwind placeholder */
            }

            /* Focus styles for content area */
            body:focus {
              outline: none;
            }
          `,
          setup: (editor) => {
            editor.on('focus', () => {
              const container = editor.getContainer()
              if (container) {
                container.classList.add('tox-focused')
              }
            })
            
            editor.on('blur', () => {
              const container = editor.getContainer()
              if (container) {
                container.classList.remove('tox-focused')
              }
            })

            editor.on('init', () => {
              try {
                if (setIsLoading) {
                  setIsLoading(false)
                }
              } catch (error) {
                console.error('Error initializing TinyMCE editor:', error)
              }
            })
          }
        }}
        {...otherProps}
      />
    </div>
  )
}

RichTextEditor.displayName = 'RichTextEditor'